import React from "react";
import { BrowserRouter as Router, Route } from "react-router-dom";
import MuiThemeProvider from "material-ui/styles/MuiThemeProvider";
import getMuiTheme from "material-ui/styles/getMuiTheme";
import colors from "./styles/colors";
import {
  createTheme,
  makeStyles,
  ThemeProvider,
} from "@material-ui/core/styles";
import SessionTimer from "./providers/sessionTimer";
import SessionTimeout from "./components/auth/SessionTimeout";
import { mainTheme as mainThemeV2 } from "./styles/theme";
import Login from "./layout/LoginPage/Login";
import NPSSurvey from "./layout/NPSSurvey";
import NPSError from "./layout/NPSError";
import SignUp from "./layout/SignUp";
import SignUpProvider from "./layout/Signup/SignUpProvider";
import SignUpPatient from "./layout/Signup/SignUpPatient";
import UserAppsViewer from "./layout/UserAppsViewer";
import AppDetailViewer from "./layout/AppDetailViewer";
import CreateNewApp from "./layout/CreateNewApp";
import UpdateAppInfo from "./layout/UpdateAppInfo";
import UpdateAppTemplate from "./layout/UpdateAppTemplate";
import librariesPage from "./layout/libraries/librariesPage";
import libraryPage from "./layout/libraries/libraryPage/libraryPage";
import ManageUsers from "./layout/ManageUsers";
import ManageUserCustomApp from "./layout/ManageUserCustomApp";
import AppSubscription from "./layout/AppSubscription";
import ViewOrganizationDetails from "./layout/ViewOrganizationDetails";
import ManageOrganization from "./layout/ManageOrganization";
import EditOrganizationInfo from "./layout/organization/EditOrganizationInfo";
import ManageOrganizationMembers from "./layout/organization/ManageOrganizationMembers";
import ManageOrgClients from "./layout/organization/ManageOrgClients";
import OrgMemberAppsViewer from "./layout/organization/OrgMemberAppsViewer";
import OrgMemberReportViewer from "./layout/organization/OrgMemberReportViewer";
import ManageOrganizationLibrary from "./layout/organization/ManageOrganizationLibrary";
import MessageInbox from "./layout/MessageInbox";
import PatientDashboard from "./layout/PatientDashboard";
import PatientAwards from "./layout/PatientAwards";
import PatientProgress from "./layout/PatientProgress";
import MyProfile from "./layout/MyProfile";
import ExternalResources from "./layout/ExternalResources/ExternalResources";
import PatientGoal from "./layout/PatientGoal";
import AddGoalProgress from "./layout/AddGoalProgress";
import ManageCustomRewards from "./layout/ManageCustomRewards";
import PatientInstructionView from "./layout/PatientInstructionView";
import CSATRatingView from "./layout/reporting/ChannelLevel/CSATRatingView";
import NPSRatingView from "./layout/reporting/ChannelLevel/NPSRatingView";
import NPSSubmitted from "./layout/NPSSubmitted";
import PatientComplianceView from "./layout/reporting/ChannelLevel/PatientComplianceReportView";
import ChannelReports from "./layout/reporting/ChannelLevel/ChannelReports";
import ManageRTMPatients from "./layout/rtm/ManageRTMPatients";
import PhysioVideoDisplay from "./layout/PhysioVideoDisplay";
import EditAppWelcomeEmail from "./layout/EditAppWelcomeEmail";

import WhiteLabelLoad from "./layout/WhiteLabelLoad";
import TimerProvider from "./providers/timerProvider";
import RouteWatcher from "./providers/routeWatcher";
import AppSettings from "./layout/AppSettings";
import ManagePrograms from "./layout/ManagePrograms";
import EditProgram from "./layout/EditProgram";
import Home from "./layout/Home";

import PasswordReset from "./layout/PasswordReset/PasswordReset";
import PasswordResetError from "./layout/PasswordReset/PasswordResetError";

import PatientActivityLogReportView from "./layout/reporting/ChannelLevel/PatientActivityLogReportView";

import Reauth from "./layout/Reauth";
import { NotificationContainer } from "react-notifications";
import OrganizationReports from "./layout/reporting/OrgReporting/OrganizationReports";
import OrganizationOverviewReport from "./layout/reporting/OrgReporting/OrganizationOverviewReport";
import OrganizationRTMReport from "./layout/reporting/OrgReporting/OrganizationRTMReport";
import ChannelRTMBilling from "./layout/reporting/ChannelLevel/ChannelRTMBilling";

const mainTheme = getMuiTheme({
  fontFamily: "Poppins",
  textTransform: "none",
  palette: {
    primary1Color: colors.primary,
    accent1Color: colors.accent,
    textColor: colors.text,
  },
  button: {
    textTransform: "none",
  },
  chip: {
    deleteIconColor: colors.text,
  },
  dropDownMenu: {
    accentColor: colors.text,
  },
});

const style = {
  container: {
    fontFamily: "Poppins",
    color: colors.text,
  },
};

export default () => (
  <MuiThemeProvider muiTheme={getMuiTheme(mainTheme)}>
    <ThemeProvider theme={mainThemeV2}>
      <Router>
        <div style={style.container}>
          <NotificationContainer />
          <SessionTimer />
          <SessionTimeout />
          <TimerProvider />
          <RouteWatcher />
          <Route exact path="/" component={Login} />
          <Route
            exact
            path="/password-reset/:token"
            component={PasswordReset}
          />
          <Route
            exact
            path="/password-reset/:appCode/:token"
            component={PasswordReset}
          />
          <Route exact path="/token-invalid" component={PasswordResetError} />
          <Route exact path="/token-invalid/:type" component={PasswordResetError} />
          <Route exact path="/reauth" component={Reauth} />
          <Route path="/login" component={Login} />
          <Route path="/Home" component={Home} />
          <Route path="/custom-ux/:appId" exact component={WhiteLabelLoad} />
          <Route path="/ux/:appId" exact component={WhiteLabelLoad} />
          <Route path="/nps/:token" component={NPSSurvey} />
          <Route path="/nps" exact component={NPSError} />
          <Route path="/nps-invalid" component={NPSError} />
          <Route path="/nps-submitted" component={NPSSubmitted} />
          <Route path="/sign-up" component={SignUp} />
          <Route path="/sign-up-provider" component={SignUpProvider} />
          <Route path="/sign-up-patient" component={SignUpPatient} />
          <Route path="/profile/:userId" exact component={MyProfile} />
          <Route path="/view-channels/" exact component={UserAppsViewer} />
          <Route path="/app-settings/:appId" exact component={AppSettings} />
          <Route
            path="/physioVideo/:videoId/:extensionId"
            exact
            component={PhysioVideoDisplay}
          />
          <Route
            path="/external-resources/:userId"
            exact
            component={ExternalResources}
          />
          <Route
            path="/view-channels/:userId"
            exact
            component={UserAppsViewer}
          />
          <Route
            path="/view-app-detail/:appId"
            exact
            component={AppDetailViewer}
          />
          <Route path="/create-app" component={CreateNewApp} />
          <Route
            path="/update-app-info/:appId"
            exact
            component={UpdateAppInfo}
          />
          <Route
            path="/update-app-template/:appId"
            exact
            component={UpdateAppTemplate}
          />
          <Route
            path="/manage-custom-rewards/:appId"
            exact
            component={ManageCustomRewards}
          />
          <Route path="/libraries/:appId" exact component={librariesPage} />
          <Route path="/new-library/:appId" exact component={libraryPage} />
          <Route path="/edit-library/:appId" exact component={libraryPage} />
          <Route path="/library/:appId" exact component={libraryPage} />
          <Route path="/manage-users/:appId" exact component={ManageUsers} />
          <Route path="/rtm/:appId" exact component={ManageRTMPatients} />
          <Route
            path="/manage-user-customapp/:appId/:userId"
            exact
            component={ManageUserCustomApp}
          />
          <Route
            path="/custom-app-goal/:appId/:userId"
            exact
            component={PatientGoal}
          />
          <Route
            path="/add-goal-progress/:goalId"
            exact
            component={AddGoalProgress}
          />
          <Route
            path="/app-subscription/:appId"
            exact
            component={AppSubscription}
          />
          <Route
            path="/view-organization-details/:orgId"
            exact
            component={ViewOrganizationDetails}
          />
          <Route path="/manage-organization" component={ManageOrganization} />
          <Route path="/manage-org-clients" component={ManageOrgClients} />
          <Route path="/org-reports/" exact component={OrganizationReports} />
          <Route
            path="/org-reports/org-overview-report"
            exact
            component={OrganizationOverviewReport}
          />{" "}
          <Route
            path="/org-reports/org-rtm-billing"
            exact
            component={OrganizationRTMReport}
          />
          <Route
            path="/edit-organization-info"
            component={EditOrganizationInfo}
          />
          <Route
            path="/manage-org-members"
            component={ManageOrganizationMembers}
          />
          <Route
            path="/org-member-apps/:memberUserId"
            exact
            component={OrgMemberAppsViewer}
          />
          <Route
            path="/org-member-report/:appId"
            exact
            component={OrgMemberReportViewer}
          />
          <Route
            path="/manage-org-library/:libAppId"
            exact
            component={ManageOrganizationLibrary}
          />
          <Route path="/message-inbox/:userId" exact component={MessageInbox} />
          <Route
            path="/patient-dashboard/:appId/:userId/:type/:customAppId"
            exact
            component={PatientDashboard}
          />
          <Route
            path="/patient-progress/:appId/:userId"
            exact
            component={PatientProgress}
          />
          <Route
            path="/patient-instruction-view/:appId/:userId"
            exact
            component={PatientInstructionView}
          />
          <Route
            path="/patient-awards/:appId/:userId"
            exact
            component={PatientAwards}
          />
          <Route path="/reports/channel" exact component={ChannelReports} />
          <Route
            path="/reports/channel/rtm-billing"
            exact
            component={ChannelRTMBilling}
          />
          <Route
            path="/reports/channel/csat-report"
            exact
            component={CSATRatingView}
          />
          <Route
            path="/reports/channel/nps-report"
            exact
            component={NPSRatingView}
          />
          <Route
            path="/reporting/patient-report/"
            exact
            component={PatientComplianceView}
          />
          <Route
            path="/reports/channel/patient-client-activity-log"
            exact
            component={PatientActivityLogReportView}
          />
          <Route
            path="/edit-custom-welcome"
            exact
            component={EditAppWelcomeEmail}
          />
          <Route path="/manage-programs/" exact component={ManagePrograms} />
          <Route path="/edit-program" exact component={EditProgram} />
          <Route path="/edit-program/:id" exact component={EditProgram} />
        </div>
      </Router>
    </ThemeProvider>
  </MuiThemeProvider>
);



// WEBPACK FOOTER //
// ./src/router.js